/**
 * useCompletionNotification - Plays notification when AI response completes
 *
 * Only triggers when:
 * 1. Browser tab is not focused (document.hidden === true)
 * 2. AI streaming response completes
 * 3. User has enabled notifications in settings
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSettingsStore } from '../stores/settingsStore';

// Preload audio for instant playback
let notificationAudio: HTMLAudioElement | null = null;
let audioContext: AudioContext | null = null;

function getNotificationAudio(): HTMLAudioElement {
  if (!notificationAudio) {
    notificationAudio = new Audio('/sounds/notification.mp3');
    notificationAudio.preload = 'auto';
  }
  return notificationAudio;
}

/**
 * Fallback: Generate a pleasant notification chime using Web Audio API
 * Used when audio file is not available
 */
function playFallbackChime(volume: number): void {
  try {
    if (!audioContext) {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const ctx = audioContext;
    const now = ctx.currentTime;

    // Create a pleasant two-tone chime
    const frequencies = [880, 1108.73]; // A5 and C#6 - major third interval
    const duration = 0.15;

    frequencies.forEach((freq, i) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, now);

      // Envelope: quick attack, gentle decay
      gainNode.gain.setValueAtTime(0, now + i * 0.08);
      gainNode.gain.linearRampToValueAtTime(volume * 0.3, now + i * 0.08 + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + i * 0.08 + duration);

      oscillator.start(now + i * 0.08);
      oscillator.stop(now + i * 0.08 + duration + 0.1);
    });
  } catch (err) {
    console.debug('Failed to play fallback chime:', err);
  }
}

export function useCompletionNotification() {
  const {
    notificationSoundEnabled,
    notificationPushEnabled,
    notificationVolume,
  } = useSettingsStore();

  // Track if we've requested notification permission
  const permissionChecked = useRef(false);

  // Request notification permission when push is enabled
  useEffect(() => {
    if (notificationPushEnabled && !permissionChecked.current && 'Notification' in window) {
      permissionChecked.current = true;
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, [notificationPushEnabled]);

  // Play notification sound
  const playSound = useCallback(() => {
    if (!notificationSoundEnabled) return;

    try {
      const audio = getNotificationAudio();
      audio.volume = notificationVolume;
      audio.currentTime = 0;
      audio.play().catch((err) => {
        // If audio file fails, use Web Audio API fallback
        console.debug('Audio file failed, using fallback chime:', err.message);
        playFallbackChime(notificationVolume);
      });
    } catch (err) {
      // Fallback to Web Audio API chime
      console.debug('Failed to play notification sound, using fallback:', err);
      playFallbackChime(notificationVolume);
    }
  }, [notificationSoundEnabled, notificationVolume]);

  // Show browser notification
  const showBrowserNotification = useCallback(() => {
    if (!notificationPushEnabled) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    try {
      new Notification('AI Response Ready', {
        body: 'Your message has been answered',
        icon: '/favicon.ico',
        tag: 'ai-response', // Prevents duplicate notifications
        silent: true, // We handle sound separately
      });
    } catch (err) {
      console.debug('Failed to show browser notification:', err);
    }
  }, [notificationPushEnabled]);

  // Handle AI response completion
  const handleCompletion = useCallback((event: CustomEvent) => {
    // Only notify if tab is hidden
    if (!document.hidden) return;

    const { messageId } = event.detail || {};
    console.debug('🔔 AI response complete, tab hidden - notifying', { messageId });

    playSound();
    showBrowserNotification();
  }, [playSound, showBrowserNotification]);

  // Subscribe to completion events
  useEffect(() => {
    window.addEventListener('aiResponseComplete', handleCompletion as EventListener);

    return () => {
      window.removeEventListener('aiResponseComplete', handleCompletion as EventListener);
    };
  }, [handleCompletion]);
}

/**
 * Test function to preview notification sound
 * Can be called from settings UI
 */
export function testNotificationSound(volume: number = 0.5): void {
  try {
    const audio = getNotificationAudio();
    audio.volume = volume;
    audio.currentTime = 0;
    audio.play().catch((err) => {
      // If audio file fails, use fallback chime
      console.debug('Audio file failed, using fallback chime:', err.message);
      playFallbackChime(volume);
    });
  } catch (err) {
    // Fallback to Web Audio API chime
    console.debug('Failed to play test sound, using fallback:', err);
    playFallbackChime(volume);
  }
}
